import type { DynamicClient } from '@dynamic-labs-sdk/client';
import { InvalidParamError } from '@dynamic-labs-sdk/client';
import {
  type TransferAmountParams,
  getWalletProviderFromWalletAccount,
  parseAmountToSmallestUnit,
} from '@dynamic-labs-sdk/client/core';

import { NotTronProviderError } from '../../errors/NotTronProviderError';
import { isTronWalletAccount } from '../../isTronWalletAccount';
import { isTronWalletProvider } from '../../isTronWalletProvider';

const TRX_DECIMALS = 6;

/**
 * Transfers native TRX or TRC20 tokens from one wallet to another.
 *
 * Converts the human-readable amount string to the smallest unit (SUN for TRX,
 * or the appropriate unit for TRC20 tokens) using string-based arithmetic
 * to avoid floating-point precision issues.
 */
export const tronTransferAmount = async (
  { amount, recipient, token, walletAccount }: TransferAmountParams,
  client: DynamicClient
): Promise<{ transactionHash: string }> => {
  if (!isTronWalletAccount(walletAccount)) {
    throw new InvalidParamError(
      'Wallet account is not a Tron wallet account'
    );
  }

  const provider = getWalletProviderFromWalletAccount(
    { walletAccount },
    client
  );

  if (!isTronWalletProvider(provider)) {
    throw new NotTronProviderError({ providerKey: provider.key });
  }

  const tronWeb = await provider.getTronWeb({ walletAccount });

  if (token?.address) {
    const decimals = token.decimals ?? TRX_DECIMALS;
    const smallestUnitAmount = parseAmountToSmallestUnit({
      amount,
      decimals,
    });

    const contract = await tronWeb.contract().at(token.address);
    const result = await contract.transfer(recipient, smallestUnitAmount).send();

    return { transactionHash: result };
  }

  const sunAmount = parseAmountToSmallestUnit({
    amount,
    decimals: TRX_DECIMALS,
  });

  const result = await tronWeb.trx.sendTransaction(
    recipient,
    Number(sunAmount),
    { address: walletAccount.address }
  );

  return { transactionHash: result.txid };
};
